import { LeagueGroupLabelFragment, LeagueTeamFragment } from '@amf/shared/types/graphql';
import classNames from 'classnames';
import Link from 'next/link';
import TeamAvatar from '@amf/shared/components/team/TeamAvatar';
import { useTranslate } from '@tolgee/react';
import { ReactNode, useMemo } from 'react';
import { LeagueLabelColor } from '@amf/shared/data/League';

export type PositionWithColor = {
  position: number;
  color: string;
};

interface LeagueTeamTableProps {
  teams: LeagueTeamFragment[];
  labels?: LeagueGroupLabelFragment[];
  prefix: string;
  title?: ReactNode;
  center?: boolean;
  simplified?: boolean;
  champion?: boolean;
  advancing?: number;
  descending?: number;
  disableWonInExtension?: boolean;
}

function LeagueTeamTable({
  teams,
  labels,
  prefix,
  title,
  center,
  simplified,
  champion,
  advancing,
  descending,
  disableWonInExtension,
}: LeagueTeamTableProps) {
  const { t } = useTranslate();

  const useLegacyLabelSystem = useMemo(() => labels?.length === 0, [labels]);
  const uniqueLabels = useMemo(() => {
    if (!labels) return [];
    return labels.reduce<LeagueGroupLabelFragment[]>((labels, label) => {
      const existingLabel = labels.find(
        ({ color, description }) => color === label.color && description === label.description,
      );

      if (Boolean(existingLabel) || !label.description) return labels;
      return [...labels, label];
    }, []);
  }, [labels]);

  const labelsByPosition = useMemo(() => {
    if (!labels) return [];
    return labels
      .map<PositionWithColor>(({ position, color }) => ({ position, color }))
      .reduce<PositionWithColor[]>((positions, position) => {
        const existingPosition = positions.find(p => p.position === position.position);
        if (Boolean(existingPosition)) return positions;
        return [...positions, position];
      }, []);
  }, [labels]);

  return (
    <div className='LeagueTeamTable'>
      {title && (
        <h2
          className={classNames('LeagueTeamTable__title', {
            'LeagueTeamTable__title--center': center,
          })}
        >
          {title}
        </h2>
      )}
      <div className='LeagueTeamTable__content'>
        <table className='LeagueTeamTable__table'>
          <thead>
            <tr>
              <th className='LeagueTeamTable__cell LeagueTeamTable__cell--header'>&nbsp;</th>
              <th className='LeagueTeamTable__cell LeagueTeamTable__cell--header LeagueTeamTable__cell--left'>
                {t('league.team.table.name')}
              </th>
              <th
                className='LeagueTeamTable__cell LeagueTeamTable__cell--header'
                title={t('league.team.table.completedMatches')}
              >
                {t('league.team.table.completedMatches.short')}
              </th>
              <th
                className='LeagueTeamTable__cell LeagueTeamTable__cell--header'
                title={t('league.team.table.wonMatches')}
              >
                {t('league.team.table.wonMatches.short')}
              </th>
              {!disableWonInExtension && (
                <th
                  className='LeagueTeamTable__cell LeagueTeamTable__cell--header'
                  title={t('league.team.table.wonMatchesInExtension')}
                >
                  {t('league.team.table.wonMatchesInExtension.short')}
                </th>
              )}
              {!simplified && (
                <th
                  className='LeagueTeamTable__cell LeagueTeamTable__cell--header'
                  title={t('league.team.table.drawMatches')}
                >
                  {t('league.team.table.drawMatches.short')}
                </th>
              )}
              <th
                className='LeagueTeamTable__cell LeagueTeamTable__cell--header'
                title={t('league.team.table.lostMatches')}
              >
                {t('league.team.table.lostMatches.short')}
              </th>
              {!simplified && (
                <>
                  <th className='LeagueTeamTable__cell LeagueTeamTable__cell--header'>
                    {t('league.team.table.score')}
                  </th>
                  <th
                    className='LeagueTeamTable__cell LeagueTeamTable__cell--header'
                    title={t('league.team.table.cards')}
                  >
                    {t('league.team.table.cards.short')}
                  </th>
                  <th className='LeagueTeamTable__cell LeagueTeamTable__cell--header'>
                    {t('league.team.table.totalPoints')}
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {teams.map((team, key) => {
              const diffGoals = team.scoredGoals - team.receivedGoals;
              const label = labelsByPosition.find(({ position }) => position === key + 1);
              return (
                <tr
                  key={team.id}
                  className={classNames(
                    {
                      'LeagueTeamTable__row--champion':
                        useLegacyLabelSystem && champion && key === 0,
                      'LeagueTeamTable__row--advancing':
                        useLegacyLabelSystem && advancing !== undefined && key < advancing,
                      'LeagueTeamTable__row--lastAdvancing':
                        useLegacyLabelSystem && advancing !== undefined && key + 1 === advancing,
                      'LeagueTeamTable__row--firstDescending':
                        useLegacyLabelSystem &&
                        descending !== undefined &&
                        teams.length - key === descending,
                      'LeagueTeamTable__row--descending':
                        useLegacyLabelSystem &&
                        descending !== undefined &&
                        teams.length - key <= descending,
                    },
                    label ? `LeagueTeamTable__row--${label?.color}` : null,
                  )}
                >
                  <td className='LeagueTeamTable__cell LeagueTeamTable__cell--order'>{key + 1}.</td>
                  <td className='LeagueTeamTable__cell LeagueTeamTable__cell--left'>
                    <Link href={`${prefix}/${team.id.split('||')[0]}`}>
                      <a className='LeagueTeamTable__team'>
                        <TeamAvatar team={team.team} />
                        {team.team.name}
                      </a>
                    </Link>
                  </td>
                  <td
                    className={classNames('LeagueTeamTable__cell', {
                      'LeagueTeamTable__cell--faded': team.completedMatches === 0,
                    })}
                  >
                    {team.completedMatches}
                  </td>
                  <td
                    className={classNames('LeagueTeamTable__cell', {
                      'LeagueTeamTable__cell--faded': team.wonMatches === 0,
                    })}
                  >
                    {team.wonMatches}
                  </td>
                  {!disableWonInExtension && (
                    <td
                      className={classNames('LeagueTeamTable__cell', {
                        'LeagueTeamTable__cell--faded': team.wonMatchesInExtension === 0,
                      })}
                    >
                      {team.wonMatchesInExtension}
                    </td>
                  )}
                  {!simplified && (
                    <td
                      className={classNames('LeagueTeamTable__cell', {
                        'LeagueTeamTable__cell--faded': team.drawMatches === 0,
                      })}
                    >
                      {team.drawMatches}
                    </td>
                  )}
                  <td
                    className={classNames('LeagueTeamTable__cell', {
                      'LeagueTeamTable__cell--faded': team.lostMatches === 0,
                    })}
                  >
                    {team.lostMatches}
                  </td>
                  {!simplified && (
                    <>
                      <td className='LeagueTeamTable__cell'>
                        {team.scoredGoals}:{team.receivedGoals}{' '}
                        {diffGoals > 0 && <small>(+{diffGoals})</small>}
                        {diffGoals <= 0 && <small>({diffGoals})</small>}
                      </td>
                      <td className='LeagueTeamTable__cell'>
                        {team.redCards} / {team.yellowCards}
                      </td>
                      <td
                        className={classNames('LeagueTeamTable__cell', {
                          'LeagueTeamTable__cell--faded': team.totalPoints === 0,
                        })}
                      >
                        {team.totalPoints}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {uniqueLabels.length > 0 && (
        <div className='LeagueTeamTable__labelList'>
          {uniqueLabels.map(label => (
            <p key={label.id} className='LeagueTeamTable__label'>
              <div
                className={classNames('LeagueTeamTable__labelColor', {
                  'LeagueTeamTable__labelColor--champion':
                    label.color === LeagueLabelColor.CHAMPION,
                  'LeagueTeamTable__labelColor--green': label.color === LeagueLabelColor.GREEN,
                  'LeagueTeamTable__labelColor--yellow': label.color === LeagueLabelColor.YELLOW,
                  'LeagueTeamTable__labelColor--orange': label.color === LeagueLabelColor.ORANGE,
                  'LeagueTeamTable__labelColor--red': label.color === LeagueLabelColor.RED,
                  'LeagueTeamTable__labelColor--gray': label.color === LeagueLabelColor.GRAY,
                })}
              >
                <span>{label.color}</span>
              </div>
              <span className='LeagueTeamTable__labelDescription'>{label.description}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

export default LeagueTeamTable;
